import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { authService } from '../../services/api';
import { ticketTypesService } from '../../services/ticketTypesService';
import { canViewPricingLogs } from './PricingHistoryPage';
import OrderAliasButton from './OrderAliasButton';
import { transactionsService } from '../../services/transactionsService';
import { venuesService } from '../../services/venuesService';
import { getApiUserCode } from '../Shared/ApiUserBadge';
import DateRangePicker, { formatRangeDate } from '../Shared/DateRangePicker';
import ResourceSidebar from '../Shared/ResourceSidebar';
import HeaderNewTabButton from '../Shared/HeaderNewTabButton';
import UserLoginTooltip from '../Shared/UserLoginTooltip';
import VendorPlatformPill from '../Shared/VendorPlatformPill';
import YearSelector, { normalizeEventYear } from '../Shared/YearSelector';
import { formatEventDateLabel } from '../Shared/eventDateFormat';
import { COMPACT_CURRENCY_COLUMN } from '../Shared/gridColumnPresets';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import '../Venues/VenuesPage.css';
import '../Info/InfoPage.css';
import './TransactionsPage.css';

ModuleRegistry.registerModules([AllCommunityModule]);

const transactionLabels = {
  orders: 'Orders',
  purchases: 'Purchases',
  payments: 'Payments',
};

const formatMoney = (value) => {
  if (value == null || value === '') return '';
  const amount = Number(value);
  if (Number.isNaN(amount)) return String(value);
  return amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const roundMoney = (value) => Math.round(Number(value) * 100) / 100;

const toNullableNumber = (value) => {
  if (value == null || value === '') return null;
  const amount = Number(value);
  return Number.isNaN(amount) ? null : amount;
};

const normalizeStatus = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'completed' || normalized === 'complete') return 'Complete';
  if (normalized === 'pending review') return 'Pending Review';
  if (normalized === 'price') return 'Price';
  if (normalized === 'cancelled' || normalized === 'canceled') return 'Cancelled';
  return value || '';
};

const PURCHASE_STATUS_OPTIONS = ['Completed', 'Price', 'Pending Review', 'Cancelled'];
const ORDER_STATUS_OPTIONS = ['Order Complete', 'Dice Pending', 'RTT', 'Transfer Now', 'Price', 'Dice Check', 'Delayed', 'To Buy', 'Void', 'Sold', 'Retransfer', 'Cancelled'];
const ORDER_SALE_POINT_OPTIONS = ['Other', 'Stubhub', 'Stubhub LND', 'Stubhub EU', 'Vivid Seats', 'Lysted', 'Skybox', 'Ticketmaster', 'AXS', 'Ticketswap'];
const TWZ_LOCATION_OPTIONS = ['New_York', 'North_America', 'World', 'Greece', 'London'];
const ORDERS_POLL_INTERVAL_MS = 15000;

const DEFAULT_PURCHASE_OPTIONS = {
  platforms: [],
  statuses: PURCHASE_STATUS_OPTIONS,
  venues: [],
  eventDates: [],
  ticketTypes: [],
  cards: [],
};

const DEFAULT_ORDER_OPTIONS = {
  salePoints: ORDER_SALE_POINT_OPTIONS,
  vendors: [],
  platforms: [],
  statuses: ORDER_STATUS_OPTIONS,
  venues: [],
  eventDates: [],
  ticketTypes: [],
  operators: [],
  users: [],
};

const getOrderUserLoginTooltipValue = (params) => {
  const row = params.data;
  if (!row?.user_id) return null;
  return [{
    userId: row.user_id,
    username: row.username || 'Unassigned',
    // Login is keyed by the ticket site (vendor); purchases have no vendor column, so fall back to platform.
    vendor: row.vendor || row.platform || '',
    platform: row.vendor ? (row.platform || '') : '',
    batchCode: row.tikwiz_order_id || '',
  }];
};

const normalizeOrderStatus = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'rtt') return 'RTT';
  if (normalized === 'transfer now') return 'Transfer Now';
  if (normalized === 'dice pending') return 'Dice Pending';
  if (normalized === 'dice check') return 'Dice Check';
  if (normalized === 'delayed') return 'Delayed';
  if (normalized === 'to buy') return 'To Buy';
  if (normalized === 'order complete' || normalized === 'order completed' || normalized === 'complete' || normalized === 'completed') return 'Order Complete';
  if (normalized === 'retransfer' || normalized === 're-transfer') return 'Retransfer';
  if (normalized === 'cancelled' || normalized === 'canceled') return 'Cancelled';
  if (normalized === 'price') return 'Price';
  if (normalized === 'void') return 'Void';
  if (normalized === 'sold') return 'Sold';
  return value || '';
};

const selectValues = (values, includeBlank = false) => {
  const seen = new Set();
  const nextValues = [];
  if (includeBlank) {
    seen.add('');
    nextValues.push('');
  }
  (values || []).forEach((value) => {
    const textValue = String(value || '').trim();
    const key = textValue.toLowerCase();
    if (textValue && !seen.has(key)) {
      seen.add(key);
      nextValues.push(textValue);
    }
  });
  return nextValues;
};

const findSelectValue = (values, value) => {
  const key = String(value || '').trim().toLowerCase();
  if (!key) return '';
  return selectValues(values, true).find((option) => option.toLowerCase() === key) || null;
};

const normalizeTextLabel = (value) => String(value || '').trim().replace(/\s+/g, ' ');

const normalizeLookupKey = (value) => normalizeTextLabel(value).toLowerCase();

const VENUE_PROMPT_RECORD_TYPES = {
  purchase: {
    label: 'purchase',
    requiresDetails: (item) => item.action === 'venue_details_required',
    recordId: (item) => item.purchase_id,
  },
  order: {
    label: 'order',
    requiresDetails: (item) => Boolean(item.requires_venue_details),
    recordId: (item) => item.order_id || item.tikwiz_order_id,
  },
};

const groupVenuesRequiringDetails = (pricingItems = [], recordType = 'purchase') => {
  const config = VENUE_PROMPT_RECORD_TYPES[recordType];
  const venuesByKey = new Map();

  pricingItems
    .filter(config.requiresDetails)
    .forEach((item) => {
      const venueName = normalizeTextLabel(item.venue_name);
      const key = item.venue_id || normalizeLookupKey(venueName);
      if (!key) return;

      const current = venuesByKey.get(key);
      const recordId = config.recordId(item);
      if (current) {
        if (!current.recordIds.includes(recordId)) current.recordIds.push(recordId);
        return;
      }

      venuesByKey.set(key, {
        venueId: item.venue_id || null,
        name: venueName,
        location: item.venue_location || '',
        country: item.venue_country || '',
        twzLocation: item.venue_twz_location || '',
        recordType,
        recordIds: [recordId],
      });
    });

  return Array.from(venuesByKey.values());
};

const prepareVenueResolutionDraft = (venue) => ({
  ...venue,
  resolutionMode: venue.venueId ? 'details' : 'create',
  canonicalVenueId: '',
});

const suggestCanonicalTicketType = (rawType, availableTypeNames = []) => {
  const normalized = normalizeLookupKey(rawType).replace(/-/g, ' ');
  const dayMatch = normalized.match(/\b(\d+)\s*day\b/);
  const hasGa = /\bga\b/.test(normalized) || normalized.includes('general admission');
  if (!dayMatch || !hasGa) return '';

  const suggestion = `${dayMatch[1]}-Day GA`;
  return availableTypeNames.find((name) => normalizeLookupKey(name) === normalizeLookupKey(suggestion)) || suggestion;
};

const findTicketTypeByName = (items, name) => (
  (items || []).find((item) => normalizeLookupKey(item.name) === normalizeLookupKey(name)) || null
);

const formatPricingTimestamp = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
};

const formatPricingChangeValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
};

const groupPricingResults = (items = []) => {
  const definitions = [
    { key: 'manual_resolution', label: 'Manual Resolution Required', matches: (item) => item.action === 'manual_resolution' },
    { key: 'priced', label: 'Priced', matches: (item) => item.action === 'priced' && !item.did_void },
    { key: 'void', label: 'VOID / To Buy', matches: (item) => Boolean(item.did_void) },
    { key: 'already_priced', label: 'Already Priced', matches: (item) => item.action === 'already_priced' },
    { key: 'skipped', label: 'Skipped', matches: (item) => item.action === 'skipped' },
  ];
  const assigned = new Set();
  const groups = definitions.map((definition) => {
    const groupItems = items.filter((item, index) => {
      if (assigned.has(index) || !definition.matches(item)) return false;
      assigned.add(index);
      return true;
    });
    return { ...definition, items: groupItems };
  }).filter((group) => group.items.length > 0);
  const otherItems = items.filter((_, index) => !assigned.has(index));
  if (otherItems.length > 0) groups.push({ key: 'other', label: 'Other', items: otherItems });
  return groups;
};

const statusToApiValue = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'completed' || normalized === 'complete') return 'Completed';
  if (normalized === 'pending review') return 'Pending Review';
  if (normalized === 'price') return 'Price';
  if (normalized === 'cancelled' || normalized === 'canceled') return 'Cancelled';
  return value || '';
};

const orderStatusToApiValue = (value) => normalizeOrderStatus(value);

const statusClassName = (value) => {
  const label = normalizeStatus(value).toLowerCase().replace(/\s+/g, '-');
  return `transactions-status-pill transactions-status-${label || 'unknown'}`;
};

const orderStatusClassName = (value) => {
  const label = normalizeOrderStatus(value).toLowerCase().replace(/\s+/g, '-');
  return `transactions-status-pill transactions-order-status-${label || 'unknown'}`;
};

const salePointClassName = (value) => {
  const normalized = String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return `transactions-sale-point-pill transactions-sale-point-${normalized || 'unknown'}`;
};

const operatorClassName = (value) => {
  const normalized = String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return `transactions-operator-pill transactions-operator-${normalized || 'unknown'}`;
};

// Empty year = All years (the default when the URL has no year).
const parseYearParam = (value) => (value ? normalizeEventYear(value) : '');

const buildPurchaseSearchParams = (
  qValue,
  showCancelledValue,
  dateRangeValue = {},
  yearValue = '',
) => {
  const nextParams = {};
  const trimmed = qValue.trim();
  if (yearValue) nextParams.year = String(normalizeEventYear(yearValue));
  if (trimmed) nextParams.q = trimmed;
  if (showCancelledValue) nextParams.showCancelled = '1';
  if (dateRangeValue.start) nextParams.dateFrom = dateRangeValue.start;
  if (dateRangeValue.end) nextParams.dateTo = dateRangeValue.end;
  return nextParams;
};

const buildOrderSearchParams = (
  qValue,
  showCancelledValue,
  dateRangeValue = {},
  eventDateRangeValue = {},
  departmentValue = '',
  yearValue = '',
) => {
  const nextParams = buildPurchaseSearchParams(qValue, showCancelledValue, dateRangeValue, yearValue);
  if (eventDateRangeValue.start) nextParams.eventDateFrom = eventDateRangeValue.start;
  if (eventDateRangeValue.end) nextParams.eventDateTo = eventDateRangeValue.end;
  if (departmentValue) nextParams.department = departmentValue;
  return nextParams;
};

const ORDER_PRICE_ALL_LIMIT = 100;

const currencyColumn = {
  headerName: 'Currency',
  field: 'currency',
  ...COMPACT_CURRENCY_COLUMN,
  editable: true,
  singleClickEdit: true,
  cellEditor: 'agSelectCellEditor',
  refData: { OTHER: 'Other' },
  cellEditorParams: {
    values: [null, 'USD', 'EUR', 'GBP', 'CHF', 'PLN', 'OTHER'],
  },
  valueFormatter: (params) => params.value === 'OTHER' ? 'Other' : (params.value || 'Unknown'),
  tooltipValueGetter: ({ data }) => data?.currency_review_reason
    ? `Currency needs review (${data.currency_review_reason}). Original: ${data.currency_raw ?? 'not supplied'}. Click to select a currency.`
    : `Original: ${data?.currency_raw ?? 'not supplied'}`,
  cellRenderer: ({ value, data }) => (
    <span>
      {value === 'OTHER' ? 'Other' : (value || 'Unknown')}
      {data?.currency_review_reason && (
        <span className="currency-review-badge" role="img" aria-label="Currency needs review">!</span>
      )}
    </span>
  ),
};

const normalizeEditableValue = (field, value) => {
  if (field === 'currency') return String(value || '').trim().toUpperCase() || null;
  if (field === 'status') return statusToApiValue(value);
  if (field === 'quantity') {
    const quantity = Number(value);
    return Number.isInteger(quantity) ? quantity : null;
  }
  if (field === 'total' || field === 'unit_price') {
    return toNullableNumber(value);
  }
  if (field === 'card' || field === 'event_date' || field === 'purchased_at') {
    return String(value || '').trim() || null;
  }
  return String(value || '').trim();
};

const normalizeOrderEditableValue = (field, value) => {
  if (field === 'currency') return String(value || '').trim().toUpperCase() || null;
  if (field === 'status') return orderStatusToApiValue(value);
  if (field === 'quantity') {
    const quantity = Number(value);
    return Number.isInteger(quantity) ? quantity : null;
  }
  if (field === 'total' || field === 'unit_price') {
    return toNullableNumber(value);
  }
  if (['order_id', 'day', 'event_date', 'operator', 'order_date', 'platform', 'notes', 'vendor'].includes(field)) {
    return String(value || '').trim() || null;
  }
  return String(value || '').trim();
};

const buildPurchaseUpdatePayload = (field, value, rowData) => {
  const normalizedValue = normalizeEditableValue(field, value);
  const payload = { [field]: normalizedValue };

  if (['quantity', 'total', 'unit_price'].includes(field)) {
    const nextRow = { ...rowData, [field]: normalizedValue };
    const quantity = Number(nextRow.quantity);
    if (!Number.isInteger(quantity) || quantity <= 0) {
      return { error: 'Qty must be a whole number greater than zero.' };
    }

    const total = toNullableNumber(nextRow.total);
    const unit = toNullableNumber(nextRow.unit_price);

    if (field === 'unit_price' && unit != null) {
      payload.total = roundMoney(unit * quantity);
    } else if ((field === 'total' || field === 'quantity') && total != null) {
      if (field === 'quantity') payload.total = roundMoney(total);
      payload.unit_price = roundMoney(total / quantity);
    } else if (field === 'quantity' && unit != null) {
      payload.total = roundMoney(unit * quantity);
    }
  }

  return { payload };
};

const buildOrderUpdatePayload = (field, value, rowData) => {
  const normalizedValue = normalizeOrderEditableValue(field, value);
  const payload = { [field]: normalizedValue };

  if (['quantity', 'total', 'unit_price'].includes(field)) {
    const nextRow = { ...rowData, [field]: normalizedValue };
    const quantity = Number(nextRow.quantity);
    if (!Number.isInteger(quantity) || quantity < 0) {
      return { error: 'Qty must be a whole number zero or greater.' };
    }

    const total = toNullableNumber(nextRow.total);
    const unit = toNullableNumber(nextRow.unit_price);

    if (field === 'unit_price' && unit != null) {
      payload.total = roundMoney(unit * quantity);
    } else if ((field === 'total' || field === 'quantity') && total != null) {
      if (field === 'quantity') payload.total = roundMoney(total);
      payload.unit_price = quantity > 0 ? roundMoney(total / quantity) : 0;
    } else if (field === 'quantity' && unit != null) {
      payload.total = roundMoney(unit * quantity);
    }
  }

  return { payload };
};

const TypeaheadSearchEditor = forwardRef((props, ref) => {
  const [value, setValue] = useState(props.value || '');
  const inputRef = useRef(null);
  const options = selectValues(props.values || [], true);
  const listId = `${props.optionListId || 'transactions-typeahead-options'}-${props.node?.id || 'editor'}`;

  useImperativeHandle(ref, () => ({
    getValue: () => value,
    afterGuiAttached: () => {
      inputRef.current?.focus();
      inputRef.current?.select();
    },
  }));

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const updateValue = (nextValue) => {
    setValue(nextValue);
    props.onValueChange?.(nextValue);
  };

  const commitCurrentValue = () => {
    const nextValue = inputRef.current?.value ?? value;
    updateValue(nextValue);
    window.setTimeout(() => props.stopEditing?.(), 0);
  };

  return (
    <>
      <input
        ref={inputRef}
        className={props.editorClassName || 'transactions-typeahead-editor'}
        list={listId}
        value={value}
        onInput={(event) => updateValue(event.target.value)}
        onChange={(event) => updateValue(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.stopPropagation();
            commitCurrentValue();
            return;
          }
          if (event.key === 'Escape') {
            event.stopPropagation();
            props.stopEditing?.(true);
            return;
          }
          event.stopPropagation();
        }}
      />
      <datalist id={listId}>
        {options.map((option) => (
          <option key={option || 'blank'} value={option} />
        ))}
      </datalist>
    </>
  );
});

TypeaheadSearchEditor.displayName = 'TypeaheadSearchEditor';

const TransactionsPage = ({ forcedType } = {}) => {
  const navigate = useNavigate();
  const { transactionType = 'orders' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedType = forcedType || transactionType;
  const activeType = transactionLabels[requestedType] ? requestedType : 'orders';
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [showCancelled, setShowCancelled] = useState(searchParams.get('showCancelled') === '1');
  const [dateRange, setDateRange] = useState({
    start: searchParams.get('dateFrom') || '',
    end: searchParams.get('dateTo') || '',
  });
  const [eventDateRange, setEventDateRange] = useState({
    start: searchParams.get('eventDateFrom') || '',
    end: searchParams.get('eventDateTo') || '',
  });
  const [department, setDepartment] = useState(searchParams.get('department') || '');
  const [selectedYear, setSelectedYear] = useState(
    parseYearParam(searchParams.get('year')),
  );
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pricing, setPricing] = useState(false);
  const [pricingModalOpen, setPricingModalOpen] = useState(false);
  const [pricingRunLoading, setPricingRunLoading] = useState(false);
  const [pricingRun, setPricingRun] = useState(null);
  const [latestCommittedPricingRun, setLatestCommittedPricingRun] = useState(null);
  const [approvedVoidOrderIds, setApprovedVoidOrderIds] = useState([]);
  const [pricingLimit, setPricingLimit] = useState(null);
  const [ticketTypeItems, setTicketTypeItems] = useState([]);
  const [aliasDraft, setAliasDraft] = useState(null);
  const [aliasSaving, setAliasSaving] = useState(false);
  const [aliasOptionsLoading, setAliasOptionsLoading] = useState(false);
  const [aliasOptionsError, setAliasOptionsError] = useState('');
  const [aliasAddedOrderIds, setAliasAddedOrderIds] = useState([]);
  const [purchaseOptions, setPurchaseOptions] = useState(DEFAULT_PURCHASE_OPTIONS);
  const [orderOptions, setOrderOptions] = useState(DEFAULT_ORDER_OPTIONS);
  const [currentUser, setCurrentUser] = useState(null);
  const [completionDraft, setCompletionDraft] = useState(null);
  const [completionProofs, setCompletionProofs] = useState([]);
  const [completionSubmitting, setCompletionSubmitting] = useState(false);
  const [completionError, setCompletionError] = useState('');
  const [proofViewerOrder, setProofViewerOrder] = useState(null);
  const [proofViewerFiles, setProofViewerFiles] = useState([]);
  const [proofViewerSelectedId, setProofViewerSelectedId] = useState('');
  const [proofViewerUrl, setProofViewerUrl] = useState('');
  const [proofViewerContentType, setProofViewerContentType] = useState('');
  const [proofViewerLoading, setProofViewerLoading] = useState(false);
  const [proofViewerError, setProofViewerError] = useState('');
  const [purchaseVenueDraft, setPurchaseVenueDraft] = useState(null);
  const [purchaseVenueQueue, setPurchaseVenueQueue] = useState([]);
  const [purchaseVenueSaving, setPurchaseVenueSaving] = useState(false);
  const [purchaseVenueError, setPurchaseVenueError] = useState('');
  const [canonicalVenues, setCanonicalVenues] = useState([]);
  const [canonicalVenuesLoading, setCanonicalVenuesLoading] = useState(false);
  const [canonicalVenuesError, setCanonicalVenuesError] = useState('');
  // What to re-run once every queued venue is saved: 'purchases', 'orders-dry-run' or 'orders-price-all'.
  const [venuePromptResume, setVenuePromptResume] = useState(null);
  const completionProofInputRef = useRef(null);
  const aliasOptionsRequestRef = useRef(null);
  const ordersRequestRef = useRef(0);
  const [currencyReviewOnly, setCurrencyReviewOnly] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const loadPurchases = async (
    qValue = query,
    showCancelledValue = showCancelled,
    dateRangeValue = dateRange,
    yearValue = selectedYear,
  ) => {
    setLoading(true);
    setError('');
    const result = await transactionsService.listPurchases({
      q: qValue.trim() || undefined,
      showCancelled: showCancelledValue,
      year: yearValue || undefined,
      dateFrom: dateRangeValue.start || undefined,
      dateTo: dateRangeValue.end || dateRangeValue.start || undefined,
      limit: 2000,
    });
    if (result.success) {
      setItems(result.items || []);
      setTotalCount(result.totalCount || 0);
    } else {
      setError(result.error);
      setItems([]);
      setTotalCount(0);
    }
    setLoading(false);
  };

  const loadPurchaseOptions = async () => {
    const result = await transactionsService.listPurchaseOptions();
    if (result.success) {
      setPurchaseOptions({
        ...DEFAULT_PURCHASE_OPTIONS,
        ...result.options,
        statuses: selectValues(result.options?.statuses?.length ? result.options.statuses : PURCHASE_STATUS_OPTIONS),
      });
    } else {
      setError(result.error);
    }
  };

  const loadOrders = async (
    qValue = query,
    showCancelledValue = showCancelled,
    dateRangeValue = dateRange,
    eventDateRangeValue = eventDateRange,
    departmentValue = department,
    yearValue = selectedYear,
    silent = false,
  ) => {
    const requestId = ++ordersRequestRef.current;
    if (!silent) {
      setLoading(true);
      setError('');
    }
    const result = await transactionsService.listOrders({
      q: qValue.trim() || undefined,
      showCancelled: showCancelledValue,
      year: yearValue || undefined,
      dateFrom: dateRangeValue.start || undefined,
      dateTo: dateRangeValue.end || dateRangeValue.start || undefined,
      eventDateFrom: eventDateRangeValue.start || undefined,
      eventDateTo: eventDateRangeValue.end || eventDateRangeValue.start || undefined,
      department: departmentValue || undefined,
      limit: 3000,
    });
    // A newer load (e.g. filters changed mid-poll) owns the grid now.
    if (requestId !== ordersRequestRef.current) return;
    if (result.success) {
      setItems(result.items || []);
      setTotalCount(result.totalCount || 0);
    } else if (!silent) {
      setError(result.error);
      setItems([]);
      setTotalCount(0);
    }
    if (!silent) setLoading(false);
  };

  const loadOrderOptions = async () => {
    const result = await transactionsService.listOrderOptions();
    if (result.success) {
      setOrderOptions({
        ...DEFAULT_ORDER_OPTIONS,
        ...result.options,
        salePoints: selectValues(result.options?.salePoints?.length ? result.options.salePoints : ORDER_SALE_POINT_OPTIONS),
        statuses: selectValues(result.options?.statuses?.length ? result.options.statuses : ORDER_STATUS_OPTIONS),
      });
    } else {
      setError(result.error);
    }
  };

  const loadCanonicalVenues = async () => {
    setCanonicalVenuesLoading(true);
    setCanonicalVenuesError('');
    const result = await venuesService.listVenues({ limit: 5000 });
    setCanonicalVenuesLoading(false);
    if (result.success) {
      setCanonicalVenues(result.items || []);
    } else {
      setCanonicalVenues([]);
      setCanonicalVenuesError(result.error);
    }
  };

  const loadTicketTypesForAlias = async () => {
    if (ticketTypeItems.length) return ticketTypeItems;
    if (aliasOptionsRequestRef.current) return aliasOptionsRequestRef.current;

    setAliasOptionsLoading(true);
    setAliasOptionsError('');
    const request = ticketTypesService.listTicketTypeOptions({ limit: 5000 })
      .then((result) => {
        if (result.success) {
          setTicketTypeItems(result.items || []);
          return result.items || [];
        }

        setAliasOptionsError(result.error);
        return [];
      })
      .finally(() => {
        aliasOptionsRequestRef.current = null;
        setAliasOptionsLoading(false);
      });
    aliasOptionsRequestRef.current = request;

    const result = await request;
    return result;
  };

  useEffect(() => {
    let isMounted = true;
    const token = authService.getToken();
    if (!token) return undefined;

    authService.getCurrentUser(token)
      .then((user) => {
        if (isMounted) setCurrentUser(user);
      })
      .catch(() => {
        if (isMounted) setCurrentUser(null);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    setProofViewerFiles([]);
    setProofViewerSelectedId('');
    setProofViewerUrl('');
    setProofViewerError('');
    if (!proofViewerOrder) return undefined;
    let isCurrent = true;
    setProofViewerLoading(true);
    transactionsService.getOrderCompletionProofs(proofViewerOrder.tikwiz_order_id)
      .then((result) => {
        if (!isCurrent) return;
        if (!result.success || !result.proofs.length) {
          setProofViewerError(result.error || 'No proofs were uploaded for this order.');
          setProofViewerLoading(false);
          return;
        }
        setProofViewerFiles(result.proofs);
        setProofViewerSelectedId(result.proofs[0].proof_id);
      });
    return () => { isCurrent = false; };
  }, [proofViewerOrder]);

  useEffect(() => {
    if (!proofViewerOrder || !proofViewerSelectedId) return undefined;
    let isCurrent = true;
    let objectUrl = '';
    setProofViewerUrl('');
    setProofViewerContentType('');
    setProofViewerLoading(true);
    setProofViewerError('');
    transactionsService.getOrderCompletionProof(proofViewerOrder.tikwiz_order_id, proofViewerSelectedId)
      .then((result) => {
        if (!isCurrent) return;
        if (!result.success) {
          setProofViewerError(result.error);
          return;
        }
        objectUrl = URL.createObjectURL(result.proof);
        setProofViewerUrl(objectUrl);
        setProofViewerContentType(result.contentType || result.proof?.type || '');
      })
      .finally(() => {
        if (isCurrent) setProofViewerLoading(false);
      });
    return () => {
      isCurrent = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [proofViewerOrder, proofViewerSelectedId]);

  useEffect(() => {
    const qValue = searchParams.get('q') || '';
    const showCancelledValue = searchParams.get('showCancelled') === '1';
    const dateRangeValue = {
      start: searchParams.get('dateFrom') || '',
      end: searchParams.get('dateTo') || '',
    };
    const eventDateRangeValue = {
      start: searchParams.get('eventDateFrom') || '',
      end: searchParams.get('eventDateTo') || '',
    };
    const departmentValue = searchParams.get('department') || '';
    const yearValue = parseYearParam(searchParams.get('year'));
    setQuery(qValue);
    setShowCancelled(showCancelledValue);
    setDateRange(dateRangeValue);
    setEventDateRange(eventDateRangeValue);
    setDepartment(departmentValue);
    setSelectedYear(yearValue);
    setAliasDraft(null);
    setCompletionDraft(null);
    setCompletionProofs([]);
    setCompletionError('');
    setProofViewerOrder(null);
    if (activeType === 'purchases') {
      loadPurchaseOptions();
      loadPurchases(qValue, showCancelledValue, dateRangeValue, yearValue);
    } else if (activeType === 'orders') {
      loadOrderOptions();
      loadOrders(qValue, showCancelledValue, dateRangeValue, eventDateRangeValue, departmentValue, yearValue);
    } else {
      setItems([]);
      setTotalCount(0);
      setError('');
      setMessage('');
      setPurchaseOptions(DEFAULT_PURCHASE_OPTIONS);
      setOrderOptions(DEFAULT_ORDER_OPTIONS);
    }
  }, [activeType, searchParams]);

  // Silently refresh the orders list so new orders show up without a manual reload.
  useEffect(() => {
    if (activeType !== 'orders') return undefined;

    const pollOrders = () => {
      if (document.visibilityState !== 'visible') return;
      loadOrders(
        searchParams.get('q') || '',
        searchParams.get('showCancelled') === '1',
        { start: searchParams.get('dateFrom') || '', end: searchParams.get('dateTo') || '' },
        { start: searchParams.get('eventDateFrom') || '', end: searchParams.get('eventDateTo') || '' },
        searchParams.get('department') || '',
        parseYearParam(searchParams.get('year')),
        true,
      );
    };

    const intervalId = window.setInterval(pollOrders, ORDERS_POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', pollOrders);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', pollOrders);
    };
  }, [activeType, searchParams]);

  const purchaseColumnDefs = useMemo(() => [
    {
      headerName: 'Purchase ID',
      field: 'purchase_id',
      minWidth: 115,
      width: 120,
      pinned: 'left',
      sort: 'desc',
      sortIndex: 0,
      comparator: (valueA, valueB) => Number(valueA || 0) - Number(valueB || 0),
      cellRenderer: (params) => (
        <button
          className="transactions-link-button"
          type="button"
          onClick={() => {
            setQuery(String(params.value || ''));
            setSearchParams(buildPurchaseSearchParams(String(params.value || ''), false, {}, selectedYear));
          }}
        >
          {params.value}
        </button>
      ),
    },
    {
      headerName: 'Status',
      field: 'status',
      minWidth: 128,
      width: 132,
      pinned: 'left',
      editable: true,
      singleClickEdit: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: selectValues(purchaseOptions.statuses?.length ? purchaseOptions.statuses : PURCHASE_STATUS_OPTIONS),
      },
      valueFormatter: (params) => normalizeStatus(params.value),
      cellRenderer: (params) => (
        <span className={statusClassName(params.value)}>{normalizeStatus(params.value)}</span>
      ),
    },
    {
      headerName: 'Source',
      field: 'source',
      minWidth: 100,
      width: 105,
    },
    {
      headerName: 'Platform',
      field: 'platform',
      minWidth: 120,
      width: 130,
      editable: true,
      singleClickEdit: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: selectValues(purchaseOptions.platforms),
      },
      cellRenderer: (params) => (
        <VendorPlatformPill kind="platform" value={params.value} />
      ),
    },
    {
      headerName: 'Event UUID',
      field: 'source_uuid',
      minWidth: 115,
      width: 120,
      cellRenderer: (params) => {
        if (!params.value) return '';
        const eventUuid = String(params.value);
        return (
          <Link
            className="transactions-link-button"
            to={`/user/events?q=${encodeURIComponent(eventUuid)}`}
            title="Open in Events"
          >
            {eventUuid}
          </Link>
        );
      },
    },
    { headerName: 'User', field: 'username', minWidth: 78, width: 78, maxWidth: 78 },
    { headerName: 'Event', field: 'event_name', minWidth: 220, flex: 1.4, editable: true, singleClickEdit: true },
    {
      headerName: 'Venue',
      field: 'venue',
      minWidth: 170,
      flex: 1,
      editable: true,
      singleClickEdit: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: selectValues(purchaseOptions.venues),
      },
    },
    {
      headerName: 'Date',
      field: 'event_date',
      minWidth: 100,
      width: 110,
      // Year only when "All" years is selected; with a single year it's redundant.
      valueFormatter: (params) => formatEventDateLabel(params.value, !selectedYear),
      editable: true,
      singleClickEdit: true,
      cellEditor: TypeaheadSearchEditor,
      cellEditorPopup: true,
      cellEditorParams: {
        values: selectValues(purchaseOptions.eventDates, true),
        optionListId: 'purchase-date-options',
        editorClassName: 'transactions-typeahead-editor',
      },
    },
    {
      headerName: 'Ticket Type',
      field: 'ticket_type',
      minWidth: 120,
      width: 130,
      editable: true,
      singleClickEdit: true,
    },
    {
      headerName: 'Qty',
      field: 'quantity',
      minWidth: 80,
      width: 80,
      type: 'numericColumn',
      editable: true,
      singleClickEdit: true,
      valueParser: (params) => Number(params.newValue),
    },
    { ...currencyColumn },
    {
      headerName: 'Unit',
      field: 'unit_price',
      minWidth: 95,
      width: 100,
      type: 'numericColumn',
      editable: true,
      singleClickEdit: true,
      valueParser: (params) => toNullableNumber(params.newValue),
      valueFormatter: (params) => formatMoney(params.value),
    },
    {
      headerName: 'Total',
      field: 'total',
      minWidth: 100,
      width: 110,
      type: 'numericColumn',
      editable: true,
      singleClickEdit: true,
      valueParser: (params) => toNullableNumber(params.newValue),
      valueFormatter: (params) => formatMoney(params.value),
    },
    {
      headerName: 'Card',
      field: 'card',
      minWidth: 140,
      editable: true,
      singleClickEdit: true,
      cellEditor: TypeaheadSearchEditor,
      cellEditorPopup: true,
      cellEditorParams: {
        values: selectValues(purchaseOptions.cards, true),
        optionListId: 'purchase-card-options',
        editorClassName: 'transactions-typeahead-editor',
      },
    },
    {
      headerName: 'Purchased At',
      field: 'purchased_at',
      minWidth: 175,
      valueFormatter: (params) => formatDateTime(params.value),
    },
  ], [purchaseOptions, selectedYear, setSearchParams]);

  const orderColumnDefs = useMemo(() => [
    {
      headerName: 'Sale Point',
      field: 'sale_point',
      minWidth: 120,
      width: 128,
      pinned: 'left',
      editable: true,
      singleClickEdit: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: selectValues(orderOptions.salePoints?.length ? orderOptions.salePoints : ORDER_SALE_POINT_OPTIONS),
      },
      cellRenderer: (params) => (
        <span className={salePointClassName(params.value)}>{params.value || ''}</span>
      ),
    },
    {
      headerName: 'Order ID',
      field: 'order_id',
      minWidth: 95,
      width: 100,
      pinned: 'left',
      editable: false,
      cellRenderer: (params) => {
        const label = params.value || params.data?.tikwiz_order_id || '';
        const isComplete = normalizeOrderStatus(params.data?.status) === 'Order Complete';
        const hasProof = isComplete && params.data?.has_completion_proof && params.data?.tikwiz_order_id;
        if (!label) return '';

        return (
          <button
            className={hasProof ? 'transactions-link-button' : 'transactions-order-copy-button'}
            type="button"
            onClick={async (event) => {
              event.stopPropagation();
              if (event.detail > 1) return;
              try {
                await navigator.clipboard.writeText(String(label));
                setMessage(`Order ID ${label} copied.`);
              } catch {
                setError('Could not copy the Order ID. Check clipboard permissions and try again.');
              }
            }}
            onDoubleClick={(event) => {
              event.stopPropagation();
              if (hasProof) setProofViewerOrder(params.data);
            }}
            onKeyDown={(event) => {
              if (hasProof && event.shiftKey && event.key === 'Enter') {
                event.preventDefault();
                event.stopPropagation();
                setProofViewerOrder(params.data);
              }
            }}
            aria-label={`Copy order ID ${label}${hasProof ? '; double-click or Shift+Enter to view completion proof' : ''}`}
            title={hasProof ? 'Click to copy Order ID; double-click or Shift+Enter to view proof' : 'Click to copy Order ID'}
          >
            {label}
          </button>
        );
      },
    },
    {
      headerName: 'Status',
      field: 'status',
      minWidth: 105,
      width: 112,
      pinned: 'left',
      editable: true,
      singleClickEdit: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: selectValues(orderOptions.statuses?.length ? orderOptions.statuses : ORDER_STATUS_OPTIONS),
      },
      valueFormatter: (params) => normalizeOrderStatus(params.value),
      cellRenderer: (params) => (
        <span className={orderStatusClassName(params.value)}>{normalizeOrderStatus(params.value)}</span>
      ),
    },
    {
      headerName: 'Operator',
      field: 'operator',
      minWidth: 64,
      width: 72,
      editable: true,
      singleClickEdit: true,
      cellEditor: TypeaheadSearchEditor,
      cellEditorPopup: true,
      cellEditorParams: {
        values: selectValues(orderOptions.operators, true),
        optionListId: 'order-operator-options',
        editorClassName: 'transactions-typeahead-editor',
      },
      cellRenderer: (params) => {
        const operator = String(params.value || '').trim();
        if (!operator) return '';
        return <span className={operatorClassName(operator)}>{operator}</span>;
      },
    },
    {
      headerName: 'User',
      field: 'username',
      minWidth: 68,
      width: 75,
      tooltipComponent: UserLoginTooltip,
      tooltipValueGetter: getOrderUserLoginTooltipValue,
    },
    {
      headerName: 'Event UUID',
      field: 'source_uuid',
      // Fixed width: min = max, so it can't be resized.
      minWidth: 115,
      width: 115,
      maxWidth: 115,
      cellRenderer: (params) => {
        if (!params.value) return '';
        const eventUuid = String(params.value).split(',')[0].trim();
        return (
          <Link
            className="transactions-link-button"
            to={`/user/events?q=${encodeURIComponent(eventUuid.replace(/[A-Za-z]+$/, ''))}`}
            title="Open in Events"
          >
            {params.value}
          </Link>
        );
      },
    },
    { headerName: 'Event', field: 'event_name', minWidth: 220, flex: 1.4, editable: true, singleClickEdit: true },
    {
      headerName: 'Venue',
      field: 'venue_name',
      minWidth: 190,
      flex: 1,
      editable: true,
      singleClickEdit: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: selectValues(orderOptions.venues),
      },
    },
    {
      headerName: 'Qty',
      headerTooltip: 'Quantity',
      field: 'quantity',
      minWidth: 52,
      width: 58,
      cellStyle: { textAlign: 'center' },
      editable: true,
      singleClickEdit: true,
      valueParser: (params) => Number(params.newValue),
    },
    { ...currencyColumn },
    {
      headerName: 'Unit Price',
      field: 'unit_price',
      minWidth: 105,
      width: 112,
      cellStyle: { textAlign: 'center' },
      editable: true,
      singleClickEdit: true,
      valueParser: (params) => toNullableNumber(params.newValue),
      valueFormatter: (params) => formatMoney(params.value),
    },
    {
      headerName: 'Type',
      field: 'type_label',
      minWidth: 130,
      width: 145,
      editable: true,
      singleClickEdit: true,
      cellEditor: TypeaheadSearchEditor,
      cellEditorPopup: true,
      cellEditorParams: {
        values: selectValues(orderOptions.ticketTypes, true),
        optionListId: 'order-type-options',
        editorClassName: 'transactions-typeahead-editor',
      },
    },
    {
      headerName: 'Event Date',
      field: 'event_date',
      minWidth: 130,
      width: 145,
      valueFormatter: (params) => formatEventDateLabel(params.value, !selectedYear),
      editable: true,
      singleClickEdit: true,
      cellEditor: TypeaheadSearchEditor,
      cellEditorPopup: true,
      cellEditorParams: {
        values: selectValues(orderOptions.eventDates, true),
        optionListId: 'order-date-options',
        editorClassName: 'transactions-typeahead-editor',
      },
    },
    {
      headerName: 'Vendor',
      field: 'vendor',
      minWidth: 125,
      width: 135,
      editable: true,
      singleClickEdit: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: selectValues(orderOptions.vendors),
      },
      cellRenderer: (params) => (
        <VendorPlatformPill kind="vendor" value={params.value} />
      ),
    },
    {
      headerName: 'Platform',
      field: 'platform',
      minWidth: 130,
      width: 138,
      editable: true,
      singleClickEdit: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: selectValues(orderOptions.platforms, true),
      },
      cellRenderer: (params) => (
        <VendorPlatformPill kind="platform" value={params.value} />
      ),
    },
    {
      headerName: 'Revenue',
      field: 'total',
      minWidth: 105,
      width: 112,
      type: 'numericColumn',
      editable: true,
      singleClickEdit: true,
      valueParser: (params) => toNullableNumber(params.newValue),
      valueFormatter: (params) => formatMoney(params.value),
    },
    {
      headerName: 'TikWiz Order ID',
      field: 'tikwiz_order_id',
      minWidth: 155,
      width: 170,
      editable: false,
      valueGetter: (params) => params.data?.tikwiz_order_id ?? '',
    },
    { headerName: 'Order Date', field: 'order_date', minWidth: 160, width: 170, editable: true, singleClickEdit: true },
    { headerName: 'Notes', field: 'notes', minWidth: 220, flex: 1.2, editable: true, singleClickEdit: true },
  ], [orderOptions, selectedYear]);

  const columnDefs = activeType === 'orders' ? orderColumnDefs : purchaseColumnDefs;

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    floatingFilter: true,
    resizable: true,
  }), []);

  const ticketTypeNames = useMemo(() => selectValues([
    ...(orderOptions.ticketTypes || []),
    ...ticketTypeItems.map((item) => item.name),
  ]), [orderOptions.ticketTypes, ticketTypeItems]);

  const openTicketTypeAliasModal = (item) => {
    setError('');
    setMessage('');
    setAliasOptionsError('');
    const availableNames = selectValues([
      ...(orderOptions.ticketTypes || []),
      ...ticketTypeItems.map((ticketType) => ticketType.name),
    ]);
    const rawType = normalizeTextLabel(item.type_label);
    setAliasDraft({
      tikwizOrderId: item.tikwiz_order_id,
      orderId: item.order_id,
      eventName: item.event_name,
      venueName: item.venue_name,
      aliasName: rawType,
      canonicalName: suggestCanonicalTicketType(rawType, availableNames),
    });

    if (!ticketTypeItems.length) {
      void loadTicketTypesForAlias().then((loadedTicketTypes) => {
        if (!loadedTicketTypes.length) return;
        const loadedNames = selectValues([
          ...(orderOptions.ticketTypes || []),
          ...loadedTicketTypes.map((ticketType) => ticketType.name),
        ]);
        setAliasDraft((current) => {
          if (!current || current.tikwizOrderId !== item.tikwiz_order_id || current.canonicalName) {
            return current;
          }
          return {
            ...current,
            canonicalName: suggestCanonicalTicketType(current.aliasName, loadedNames),
          };
        });
      });
    }
  };

  const handleSaveTicketTypeAlias = async (event) => {
    event.preventDefault();
    if (!aliasDraft || aliasSaving || aliasOptionsLoading) return;

    const aliasName = normalizeTextLabel(aliasDraft.aliasName);
    const canonicalName = normalizeTextLabel(aliasDraft.canonicalName);
    if (!aliasName || !canonicalName) {
      setError('Alias and canonical ticket type are required.');
      return;
    }

    setAliasSaving(true);
    setError('');

    let availableTicketTypes = ticketTypeItems;
    if (!availableTicketTypes.length) {
      availableTicketTypes = await loadTicketTypesForAlias();
    }

    let canonicalTicketType = findTicketTypeByName(availableTicketTypes, canonicalName);
    if (!canonicalTicketType) {
      availableTicketTypes = await loadTicketTypesForAlias();
      canonicalTicketType = findTicketTypeByName(availableTicketTypes, canonicalName);
    }

    if (!canonicalTicketType) {
      setAliasSaving(false);
      setError('Canonical ticket type must be selected from the existing ticket type list.');
      return;
    }

    const result = await ticketTypesService.addAlias(
      canonicalTicketType.ticket_type_id,
      aliasName,
      'order_pricing',
      aliasDraft.tikwizOrderId,
    );
    setAliasSaving(false);

    if (!result.success) {
      setError(result.error);
      return;
    }

    if (!result.orderUpdated) {
      setError('Alias was saved, but the selected order was not updated. Refresh the pricer and try again.');
      return;
    }

    setAliasAddedOrderIds((current) => (
      current.includes(aliasDraft.tikwizOrderId)
        ? current
        : [...current, aliasDraft.tikwizOrderId]
    ));
    setAliasDraft(null);
    setPricingRun((current) => (
      current?.isDryRun ? { ...current, previewInvalidated: true } : current
    ));
    setApprovedVoidOrderIds([]);
    setMessage(`${result.message || 'Alias saved.'} ${aliasName} -> ${canonicalTicketType.name}. Order updated. Run Dry Run again before pricing.`);
    await loadOrderOptions();
    await loadOrders(query, showCancelled, dateRange, eventDateRange, department);
  };

  const handleFilter = (event) => {
    event.preventDefault();
    setSearchParams(
      activeType === 'orders'
        ? buildOrderSearchParams(query, showCancelled, dateRange, eventDateRange, department, selectedYear)
        : buildPurchaseSearchParams(query, showCancelled, dateRange, selectedYear),
    );
  };

  const handleToggleCancelled = () => {
    const nextShowCancelled = !showCancelled;
    setShowCancelled(nextShowCancelled);
    setSearchParams(
      activeType === 'orders'
        ? buildOrderSearchParams(query, nextShowCancelled, dateRange, eventDateRange, department, selectedYear)
        : buildPurchaseSearchParams(query, nextShowCancelled, dateRange, selectedYear),
    );
  };

  const closeCompletionModal = () => {
    if (completionSubmitting) return;
    setCompletionDraft(null);
    setCompletionProofs([]);
    setCompletionError('');
    if (completionProofInputRef.current) completionProofInputRef.current.value = '';
  };

  const handleCompletionProofChange = (event) => {
    const files = Array.from(event.target.files || []);
    setCompletionError('');
    if (completionProofs.length + files.length > 10) {
      setCompletionError('Upload at most 10 proof files.');
      return;
    }
    if (files.some((file) => file.size > 10 * 1024 * 1024)) {
      setCompletionError('Each proof must be 10 MB or smaller.');
      return;
    }
    setCompletionProofs((previous) => [...previous, ...files]);
  };

  const openCompletionProofPicker = () => {
    if (completionSubmitting || !completionProofInputRef.current) return;
    // Clearing first lets the user choose the same file again after an error.
    completionProofInputRef.current.value = '';
    completionProofInputRef.current.click();
  };

  const handleCompleteOrder = async (event) => {
    event.preventDefault();
    if (!completionDraft || completionSubmitting) return;

    setCompletionSubmitting(true);
    setCompletionError('');
    setError('');
    setMessage('');
    const result = await transactionsService.completeOrder(
      completionDraft.order.tikwiz_order_id,
      completionProofs,
    );
    setCompletionSubmitting(false);

    if (!result.success) {
      setCompletionError(result.error);
      return;
    }

    setItems((currentItems) => currentItems.map((item) => (
      item.tikwiz_order_id === completionDraft.order.tikwiz_order_id ? result.item : item
    )));
    completionDraft.rowNode?.setData(result.item);
    setMessage(result.message || `Order ${completionDraft.order.order_id || completionDraft.order.tikwiz_order_id} completed.`);
    closeCompletionModal();
  };

  const handleCellValueChanged = async (params) => {
    const field = params.colDef.field;
    if (activeType === 'orders') {
      const orderId = params.data?.tikwiz_order_id;
      if (!orderId || !field || ['tikwiz_order_id', 'source_uuid', 'event_ref', 'username', 'open_orders', 'created_at'].includes(field)) return;

      const oldValue = normalizeOrderEditableValue(field, params.oldValue);
      const newValue = normalizeOrderEditableValue(field, params.newValue);
      if (oldValue === newValue) return;

      if (field === 'status' && newValue === 'Order Complete') {
        const order = { ...params.data, status: params.oldValue };
        params.node.setData(order);
        setCompletionDraft({ order, rowNode: params.node });
        setCompletionProofs([]);
        setCompletionError('');
        if (completionProofInputRef.current) completionProofInputRef.current.value = '';
        setError('');
        setMessage('');
        return;
      }

      if (field === 'venue_name' && newValue && findSelectValue(orderOptions.venues, newValue) === null) {
        setError('Venue must be selected from the existing venue list.');
        params.node.setData({ ...params.data, [field]: params.oldValue });
        return;
      }

      if (field === 'event_date' && newValue && findSelectValue(orderOptions.eventDates, newValue) === null) {
        setError('Date must be selected from the existing date list.');
        params.node.setData({ ...params.data, [field]: params.oldValue });
        return;
      }

      const { payload, error: payloadError } = buildOrderUpdatePayload(field, newValue, params.data);
      if (payloadError) {
        setError(payloadError);
        params.node.setData({ ...params.data, [field]: params.oldValue });
        return;
      }

      setError('');
      setMessage('');
      const result = await transactionsService.updateOrder(orderId, payload);

      if (!result.success) {
        setError(result.error);
        params.node.setData({ ...params.data, [field]: params.oldValue });
        return;
      }

      if (normalizeOrderStatus(result.item.status) === 'Cancelled' && !showCancelled) {
        setItems((currentItems) => currentItems.filter((item) => item.tikwiz_order_id !== orderId));
      } else {
        setItems((currentItems) => currentItems.map((item) => (
          item.tikwiz_order_id === orderId ? result.item : item
        )));
        params.node.setData(result.item);
      }
      setMessage(result.message || `Order ${orderId} updated.`);
      return;
    }

    const purchaseId = params.data?.purchase_id;
    if (!purchaseId || !field || ['purchase_id', 'source', 'source_uuid', 'username', 'purchased_at'].includes(field)) return;

    const oldValue = normalizeEditableValue(field, params.oldValue);
    const newValue = normalizeEditableValue(field, params.newValue);
    if (oldValue === newValue) return;

    if (field === 'card' && newValue && findSelectValue(purchaseOptions.cards, newValue) === null) {
      setError('Card must be selected from the existing card list.');
      params.node.setData({ ...params.data, [field]: params.oldValue });
      return;
    }

    if (field === 'event_date' && newValue && findSelectValue(purchaseOptions.eventDates, newValue) === null) {
      setError('Date must be selected from the existing date list.');
      params.node.setData({ ...params.data, [field]: params.oldValue });
      return;
    }

    const { payload, error: payloadError } = buildPurchaseUpdatePayload(field, newValue, params.data);
    if (payloadError) {
      setError(payloadError);
      params.node.setData({ ...params.data, [field]: params.oldValue });
      return;
    }

    setError('');
    setMessage('');
    const result = await transactionsService.updatePurchase(purchaseId, payload);

    if (!result.success) {
      setError(result.error);
      params.node.setData({ ...params.data, [field]: params.oldValue });
      return;
    }

    if (statusToApiValue(result.item.status) === 'Cancelled' && !showCancelled) {
      setItems((currentItems) => currentItems.filter((item) => item.purchase_id !== purchaseId));
    } else {
      setItems((currentItems) => currentItems.map((item) => (
        item.purchase_id === purchaseId ? result.item : item
      )));
      params.node.setData(result.item);
    }
    setMessage(result.message || `Purchase ${purchaseId} updated.`);

    if (field === 'card') {
      await loadPurchases(query, showCancelled, dateRange);
    }
  };

  const runPurchasePricing = async () => {
    setPricing(true);
    setError('');
    setMessage('');
    const result = await transactionsService.pricePurchases({ dryRun: false });
    setPricing(false);

    if (!result.success) {
      setError(result.error);
      return;
    }

    const venuesRequiringDetails = groupVenuesRequiringDetails(result.items, 'purchase');
    const detailsMessage = venuesRequiringDetails.length
      ? ` ${venuesRequiringDetails.length} venue${venuesRequiringDetails.length === 1 ? '' : 's'} require location details before their purchases can be priced.`
      : '';
    setMessage(`${result.message} Created: ${result.createdEvents}. Attached: ${result.attachedEvents}. Units: ${result.unitsCreated}. Skipped: ${result.skippedCount}.${detailsMessage}`);
    await loadPurchases(query, showCancelled, dateRange);

    openVenuePrompt(venuesRequiringDetails, 'purchases');
  };

  const openVenuePrompt = (venues, resumeMode) => {
    if (!venues.length) return;
    setPurchaseVenueDraft(prepareVenueResolutionDraft(venues[0]));
    setPurchaseVenueQueue(venues.slice(1));
    setPurchaseVenueError('');
    setCanonicalVenuesError('');
    setVenuePromptResume(resumeMode);
    void loadCanonicalVenues();
  };

  // Opens the shared venue prompt for orders skipped over an unknown venue and
  // returns a message suffix describing how many venues need details.
  const promptForOrderVenues = (items, resumeMode) => {
    const venues = groupVenuesRequiringDetails(items, 'order');
    openVenuePrompt(venues, resumeMode);
    return venues.length
      ? ` ${venues.length} venue${venues.length === 1 ? '' : 's'} must be added before ${venues.length === 1 ? 'its' : 'their'} orders can be priced.`
      : '';
  };

  const handlePricePurchases = async () => {
    const confirmed = window.confirm('Price all purchases currently in Price status?');
    if (!confirmed) return;

    await runPurchasePricing();
  };

  const closePurchaseVenuePrompt = () => {
    if (purchaseVenueSaving) return;
    setPurchaseVenueDraft(null);
    setPurchaseVenueQueue([]);
    setPurchaseVenueError('');
    setCanonicalVenuesError('');
    setVenuePromptResume(null);
  };

  const handlePurchaseVenueFieldChange = (field, value) => {
    setPurchaseVenueDraft((current) => (current ? { ...current, [field]: value } : current));
  };

  const handleSavePurchaseVenue = async (event) => {
    event.preventDefault();
    if (!purchaseVenueDraft) return;

    const name = purchaseVenueDraft.name.trim();
    const isAliasResolution = !purchaseVenueDraft.venueId
      && purchaseVenueDraft.resolutionMode === 'alias';
    const location = purchaseVenueDraft.location.trim();
    const country = purchaseVenueDraft.country.trim();
    const twzLocation = purchaseVenueDraft.twzLocation.trim();
    if (!name) {
      setPurchaseVenueError('Venue name is required.');
      return;
    }
    if (isAliasResolution && !purchaseVenueDraft.canonicalVenueId) {
      setPurchaseVenueError('Select the existing canonical venue for this alias.');
      return;
    }
    if (!isAliasResolution && (!location || !country || !twzLocation)) {
      setPurchaseVenueError('Venue name, Location, Country, and TWZ Location are required.');
      return;
    }

    setPurchaseVenueSaving(true);
    setPurchaseVenueError('');
    let result;
    if (isAliasResolution) {
      result = await venuesService.addAlias(purchaseVenueDraft.canonicalVenueId, name, 'pricing');
    } else if (purchaseVenueDraft.venueId) {
      result = await venuesService.updateVenue(purchaseVenueDraft.venueId, {
        location,
        country,
        twz_location: twzLocation,
      });
    } else {
      result = await venuesService.createVenue({
        name,
        location,
        country,
        capacity: null,
        twz_location: twzLocation,
      });
    }
    setPurchaseVenueSaving(false);

    if (!result.success) {
      setPurchaseVenueError(result.error);
      return;
    }

    if (!isAliasResolution && !purchaseVenueDraft.venueId && result.item) {
      setCanonicalVenues((current) => (
        [...current.filter((venue) => venue.venue_id !== result.item.venue_id), result.item]
          .sort((left, right) => left.name.localeCompare(right.name))
      ));
    }

    const [nextVenue, ...remainingVenues] = purchaseVenueQueue;
    if (nextVenue) {
      setPurchaseVenueDraft(prepareVenueResolutionDraft(nextVenue));
      setPurchaseVenueQueue(remainingVenues);
      if (purchaseVenueDraft.recordType === 'order') await loadOrderOptions();
      else await loadPurchaseOptions();
      return;
    }

    const resumeMode = venuePromptResume;
    const recordType = purchaseVenueDraft.recordType;
    setPurchaseVenueDraft(null);
    setPurchaseVenueQueue([]);
    setVenuePromptResume(null);
    if (recordType === 'order') await loadOrderOptions();
    else await loadPurchaseOptions();
    if (resumeMode === 'orders-dry-run') {
      await handleDryRunOrders();
    } else if (resumeMode === 'orders-price-all') {
      await handlePriceAll();
    } else {
      await runPurchasePricing();
    }
  };

  const loadLatestPricingRun = async () => {
    setPricingRunLoading(true);
    setError('');
    const result = await transactionsService.getLatestOrderPricingRun();
    setPricingRunLoading(false);
    if (!result.success) {
      setError(result.error);
      return;
    }
    const latestRun = result.run ? { ...result.run, isDryRun: false } : null;
    setLatestCommittedPricingRun(latestRun);
    setPricingRun(latestRun);
  };

  const openPricingModal = () => {
    setPricingLimit(null);
    setPricingModalOpen(true);
    setPricingRun(null);
    setAliasDraft(null);
    setApprovedVoidOrderIds([]);
    if (!ticketTypeItems.length) void loadTicketTypesForAlias();
    void loadLatestPricingRun();
  };

  const handleDryRunOrders = async () => {
    setPricing(true);
    setError('');
    setMessage('');
    setPricingRun(null);
    setApprovedVoidOrderIds([]);
    const result = await transactionsService.priceOrders({ dryRun: true, limit: pricingLimit || undefined });
    setPricing(false);

    if (!result.success) {
      setError(result.error);
      return;
    }

    setPricingRun({ ...result, isDryRun: true, runStatus: 'dry_run' });
    const venueMessage = promptForOrderVenues(result.items, 'orders-dry-run');
    if (venueMessage) setMessage(venueMessage.trim());
  };

  const handlePriceOrders = async () => {
    if (!pricingRun?.isDryRun || pricingRun.previewInvalidated || pricingRun.processedCount <= 0) {
      setError('Run a successful Dry Run with Price orders before committing pricing.');
      return;
    }

    const voidApprovalText = approvedVoidOrderIds.length
      ? ` This includes ${approvedVoidOrderIds.length} explicitly approved VOID / To Buy order(s).`
      : ' No VOID units will be written without explicit approval.';
    const pricingScopeText = pricingLimit
      ? `the most recent ${pricingLimit} orders currently in Price status`
      : 'all orders currently in Price status';
    const confirmed = window.confirm(`Commit pricing for ${pricingScopeText}?${voidApprovalText}`);
    if (!confirmed) return;

    setPricing(true);
    setError('');
    setMessage('');
    const result = await transactionsService.priceOrders({
      dryRun: false,
      approvedVoidOrderIds,
      limit: pricingLimit || undefined,
    });
    setPricing(false);

    if (!result.success) {
      setError(result.error);
      return;
    }

    setPricingRun({ ...result, isDryRun: false });
    setLatestCommittedPricingRun({ ...result, isDryRun: false });
    setApprovedVoidOrderIds([]);
    const venueMessage = promptForOrderVenues(result.items, 'orders-dry-run');
    setMessage(`${result.message} Units claimed: ${result.unitsClaimed}. Already priced: ${result.alreadyPricedCount}. Skipped: ${result.skippedCount}.${venueMessage}`);
    await loadOrders(query, showCancelled, dateRange);
  };

  const handlePriceAll = async () => {
    setPricing(true);
    setError('');
    setMessage('');
    setApprovedVoidOrderIds([]);
    setPricingLimit(ORDER_PRICE_ALL_LIMIT);

    const dryRunResult = await transactionsService.priceOrders({
      dryRun: true,
      limit: ORDER_PRICE_ALL_LIMIT,
    });
    if (!dryRunResult.success) {
      setPricing(false);
      setError(dryRunResult.error);
      return;
    }

    const preview = { ...dryRunResult, isDryRun: true, runStatus: 'dry_run' };
    setPricingRun(preview);
    if (dryRunResult.processedCount <= 0) {
      setPricing(false);
      setMessage('No orders in Price status found among the most recent orders.');
      return;
    }

    if (dryRunResult.voidRequiredCount > 0) {
      setPricing(false);
      setPricingModalOpen(true);
      setAliasDraft(null);
      const venueMessage = promptForOrderVenues(dryRunResult.items, 'orders-dry-run');
      setMessage(`${dryRunResult.voidRequiredCount} order(s) need VOID approval. Review the dry-run results in the pricer.${venueMessage}`);
      return;
    }

    const result = await transactionsService.priceOrders({
      dryRun: false,
      limit: ORDER_PRICE_ALL_LIMIT,
    });
    setPricing(false);
    if (!result.success) {
      setError(result.error);
      return;
    }

    setPricingRun({ ...result, isDryRun: false });
    setLatestCommittedPricingRun({ ...result, isDryRun: false });
    const priceAllSummary = [
      `Price All: ${result.pricedCount + result.toBuyCount} priced`,
      `${result.unitsClaimed} units`,
      `${result.skippedCount} skipped`,
      `${Math.min(result.processedCount, ORDER_PRICE_ALL_LIMIT)} checked`,
    ];
    if (result.voidRequiredCount > 0) {
      priceAllSummary.push(`${result.voidRequiredCount} need VOID approval`);
    }
    if (result.manualResolutionCount > 0) {
      priceAllSummary.push(`${result.manualResolutionCount} manual`);
    }
    const venueMessage = promptForOrderVenues(result.items, 'orders-price-all');
    setMessage(`${priceAllSummary.join(' · ')}.${venueMessage}`);
    await loadOrders();
  };

  const toggleVoidApproval = (tikwizOrderId) => {
    setApprovedVoidOrderIds((current) => (
      current.includes(tikwizOrderId)
        ? current.filter((item) => item !== tikwizOrderId)
        : [...current, tikwizOrderId]
    ));
  };

  const handleUndoLastPricer = async () => {
    const confirmed = window.confirm('Undo every order and unit change made by the last Price Orders run?');
    if (!confirmed) return;

    setPricing(true);
    setError('');
    setMessage('');
    const result = await transactionsService.undoLatestOrderPricingRun();
    setPricing(false);

    if (!result.success) {
      setError(result.error);
      return;
    }

    setPricingRun({ ...result, isDryRun: false });
    setLatestCommittedPricingRun({ ...result, isDryRun: false });
    setMessage(result.message || 'The last order pricing run was undone.');
    await loadOrders(query, showCancelled, dateRange);
  };

  const proofViewerIsPdf = proofViewerContentType.toLowerCase().startsWith('application/pdf');
  const proofViewerIndex = proofViewerFiles.findIndex((file) => file.proof_id === proofViewerSelectedId);
  const selectedProof = proofViewerFiles[proofViewerIndex];
  const navigateProof = (direction) => {
    const nextProof = proofViewerFiles[proofViewerIndex + direction];
    if (!nextProof) return;
    setProofViewerUrl('');
    setProofViewerError('');
    setProofViewerLoading(true);
    setProofViewerSelectedId(nextProof.proof_id);
  };


  return (
    <div className="venues-page resource-page-with-sidebar info-page transactions-page">
      <ResourceSidebar section="transactions" />
      <header className="venues-header">
        <div className="venues-header-content">
          <HeaderNewTabButton />
          <h1>{transactionLabels[activeType]} Transactions</h1>
          <div className="venues-header-actions">
            {(activeType === 'purchases' || activeType === 'orders') && (
              <button
                className="btn btn-primary transactions-manual-entry-button"
                type="button"
                onClick={() => navigate(`/user/manual-entry?origin=transactions&type=${activeType === 'orders' ? 'order' : 'purchase'}&returnTo=${encodeURIComponent(`/user/transactions/${activeType}`)}`)}
              >
                Manual Entry
              </button>
            )}
            <button className="btn btn-secondary" type="button" onClick={() => navigate('/user')}>
              Back to Dashboard
            </button>
          </div>
        </div>
      </header>

      <main className="venues-main">
        {activeType !== 'purchases' && activeType !== 'orders' ? (
          <div className="transactions-placeholder">
            {`${transactionLabels[activeType]} will be wired in this Transactions section next.`}
          </div>
        ) : (
          <>
            <div className="transactions-toolbar">
              <form className="transactions-filter-form" onSubmit={handleFilter}>
                <YearSelector
                  id="transactions-event-year"
                  allowAll
                  value={selectedYear}
                  onChange={(nextYear) => {
                    setSelectedYear(nextYear);
                    setSearchParams(
                      activeType === 'orders'
                        ? buildOrderSearchParams(query, showCancelled, dateRange, eventDateRange, department, nextYear)
                        : buildPurchaseSearchParams(query, showCancelled, dateRange, nextYear),
                    );
                  }}
                  className="transactions-year-filter"
                />
                <div className="form-group transactions-date-filter events-date-form-group">
                  <label htmlFor="transactions-date-range">
                    {activeType === 'orders' ? 'Order Date' : 'Purchase Date'}
                  </label>
                  <DateRangePicker
                    id="transactions-date-range"
                    value={dateRange}
                    onChange={(nextRange) => {
                      setDateRange(nextRange);
                      setSearchParams(
                        activeType === 'orders'
                          ? buildOrderSearchParams(query, showCancelled, nextRange, eventDateRange, department, selectedYear)
                          : buildPurchaseSearchParams(query, showCancelled, nextRange, selectedYear),
                      );
                    }}
                  />
                </div>
                {activeType === 'orders' && (
                  <div className="form-group transactions-date-filter events-date-form-group">
                    <label htmlFor="transactions-event-date-range">Event Date</label>
                    <DateRangePicker
                      id="transactions-event-date-range"
                      value={eventDateRange}
                      onChange={(nextRange) => {
                        setEventDateRange(nextRange);
                        setSearchParams(buildOrderSearchParams(query, showCancelled, dateRange, nextRange, department, selectedYear));
                      }}
                    />
                  </div>
                )}
                <div className="form-group transactions-search-filter">
                  <label htmlFor="transactions-query">Search</label>
                  <input
                    id="transactions-query"
                    type="text"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder={activeType === 'orders' ? 'order id, event, venue, user, UUID...' : 'purchase id, event, venue, user, UUID...'}
                  />
                </div>
                {activeType === 'orders' && (
                  <div className="form-group transactions-department-filter">
                    <label htmlFor="transactions-department">Department</label>
                    <select
                      id="transactions-department"
                      value={department}
                      onChange={(event) => setDepartment(event.target.value)}
                    >
                      <option value="">All</option>
                      <option value="US">US</option>
                      <option value="World">World</option>
                      <option value="UK">UK</option>
                    </select>
                  </div>
                )}
                <button className="btn btn-primary" type="submit">
                  Apply
                </button>
              </form>

              <div className="transactions-action-box">
                <span className="transactions-action-label">Actions</span>
                <div className="transactions-action-buttons">
                  {activeType === 'purchases' && (
                    <>
                      <button
                        className="btn btn-primary transactions-price-button"
                        type="button"
                        onClick={handlePricePurchases}
                        disabled={pricing || loading}
                      >
                        {pricing ? 'Pricing...' : 'Price Purchases'}
                      </button>
                      <button
                        className="btn btn-secondary transactions-toggle-button"
                        type="button"
                        onClick={handleToggleCancelled}
                      >
                        {showCancelled ? 'Hide Cancelled' : 'Show Cancelled'}
                      </button>
                    </>
                  )}
                  {activeType === 'orders' && (
                    <>
                      <button
                        className="btn btn-primary transactions-price-button"
                        type="button"
                        onClick={handlePriceAll}
                        disabled={pricing || loading}
                      >
                        {pricing ? 'Pricing...' : 'Price All'}
                      </button>
                      <button
                        className="btn btn-secondary transactions-price-button"
                        type="button"
                        onClick={openPricingModal}
                        disabled={pricing || loading}
                      >
                        Open Pricer
                      </button>
                      <button
                        className="btn btn-secondary transactions-toggle-button"
                        type="button"
                        onClick={handleToggleCancelled}
                      >
                        {showCancelled ? 'Hide Complete & Cancelled' : 'Show Complete & Cancelled'}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>

            <label className="currency-review-filter">
              <input type="checkbox" checked={currencyReviewOnly}
                onChange={(event) => setCurrencyReviewOnly(event.target.checked)} />
              {' '}Currency needs review (within current results)
            </label>
            <div className="venues-summary">
              <strong>Showing:</strong> {(currencyReviewOnly ? items.filter((item) => item.currency_review_reason) : items).length} {activeType}
              <span className="venues-summary-tag">Event year: {selectedYear || 'All'}</span>
              <span className="venues-summary-tag">Highest ID first</span>
              {!showCancelled && (
                <span className="venues-summary-tag">
                  {activeType === 'orders' ? 'Complete & cancelled hidden' : 'Cancelled hidden'}
                </span>
              )}
              {!!totalCount && <span className="venues-summary-tag">Total: {totalCount}</span>}
              {query.trim() && <span className="venues-summary-tag">Search: {query.trim()}</span>}
              {dateRange.start && (
                <span className="venues-summary-tag">
                  {activeType === 'orders' ? 'Order dates' : 'Purchase dates'}: {formatRangeDate(dateRange.start)}{dateRange.end && dateRange.end !== dateRange.start ? ` – ${formatRangeDate(dateRange.end)}` : ''}
                </span>
              )}
              {activeType === 'orders' && eventDateRange.start && (
                <span className="venues-summary-tag">
                  Event dates: {formatRangeDate(eventDateRange.start)}{eventDateRange.end && eventDateRange.end !== eventDateRange.start ? ` – ${formatRangeDate(eventDateRange.end)}` : ''}
                </span>
              )}
              {activeType === 'orders' && department && (
                <span className="venues-summary-tag">Department: {department}</span>
              )}
            </div>

            {error && <div className="notification error">{error}</div>}
            {message && <div className="notification success">{message}</div>}

            {loading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading {activeType}...</p>
              </div>
            ) : (
              <div className="venues-grid-shell">
                <div className="ag-theme-quartz-dark venues-ag-grid transactions-ag-grid">
                  <AgGridReact
                    theme="legacy"
                    rowData={currencyReviewOnly ? items.filter((item) => item.currency_review_reason) : items}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    onCellValueChanged={handleCellValueChanged}
                    animateRows={true}
                    alwaysShowHorizontalScroll={true}
                    tooltipShowDelay={250}
                    tooltipHideDelay={8000}
                    tooltipInteraction={true}
                    pagination={true}
                    paginationPageSize={100}
                    getRowId={(params) => String(activeType === 'orders' ? params.data.tikwiz_order_id : params.data.purchase_id)}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </main>

      {completionDraft && (
        <div className="transactions-pricer-backdrop" role="presentation">
          <form
            className="transactions-completion-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="transactions-completion-title"
            onSubmit={handleCompleteOrder}
          >
            <div className="transactions-pricer-header">
              <div>
                <h2 id="transactions-completion-title">Complete Order?</h2>
                <p>Confirm the order details and attach proof of delivery.</p>
              </div>
              <button
                className="btn btn-secondary"
                type="button"
                onClick={closeCompletionModal}
                disabled={completionSubmitting}
              >
                Close
              </button>
            </div>

            <div className="transactions-completion-body">
              <dl className="transactions-completion-details">
                <div>
                  <dt>Order ID</dt>
                  <dd>{completionDraft.order.order_id || completionDraft.order.tikwiz_order_id}</dd>
                </div>
                <div>
                  <dt>Event</dt>
                  <dd>{completionDraft.order.event_name || 'Unknown event'}</dd>
                </div>
                <div>
                  <dt>Quantity</dt>
                  <dd>{completionDraft.order.quantity ?? '—'}</dd>
                </div>
                <div>
                  <dt>Unit Price</dt>
                  <dd>{formatMoney(completionDraft.order.unit_price) || '—'}</dd>
                </div>
              </dl>

              <div className="transactions-completion-proof">
                <div>
                  <strong>Proof files <span>(optional)</span></strong>
                  <p>PNG, JPG, JPEG, WebP, GIF, or PDF; up to 10 files, maximum 10 MB each.</p>
                </div>
                <input
                  ref={completionProofInputRef}
                  id="transactions-completion-proof-input"
                  className="transactions-completion-file-input"
                  type="file"
                  multiple
                  accept=".png,.jpg,.jpeg,.webp,.gif,.pdf,image/png,image/jpeg,image/webp,image/gif,application/pdf"
                  onChange={handleCompletionProofChange}
                  disabled={completionSubmitting}
                />
                <button
                  className={`btn btn-secondary transactions-completion-upload${completionSubmitting ? ' disabled' : ''}`}
                  type="button"
                  onClick={openCompletionProofPicker}
                  disabled={completionSubmitting}
                >
                  {completionProofs.length ? 'Add Proofs' : 'Upload Proofs'}
                </button>
                {completionProofs.map((file, index) => (
                  <div className="transactions-completion-file-name" key={`${file.name}-${index}`}>
                    {file.name} · {(file.size / 1024).toFixed(1)} KB
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={completionSubmitting}
                      aria-label={`Remove ${file.name}`}
                      onClick={() => setCompletionProofs((files) => files.filter((_, i) => i !== index))}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>

              <p className="transactions-completion-warning">
                Confirming will mark the linked ticket units as Delivered and record {completionDraft.order.operator || getApiUserCode(currentUser?.email)} as the transfer operator.
              </p>
              {completionError && (
                <div className="notification error transactions-completion-error" role="alert">
                  {completionError}
                </div>
              )}
            </div>

            <div className="transactions-pricer-actions">
              <button
                className="btn btn-secondary"
                type="button"
                onClick={closeCompletionModal}
                disabled={completionSubmitting}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                type="submit"
                disabled={completionSubmitting}
              >
                {completionSubmitting ? 'Completing...' : 'Confirm Order Complete'}
              </button>
            </div>
          </form>
        </div>
      )}

      {proofViewerOrder && (
        <div className="transactions-pricer-backdrop" role="presentation">
          <section
            className="transactions-completion-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="transactions-proof-viewer-title"
          >
            <div className="transactions-pricer-header">
              <div>
                <h2 id="transactions-proof-viewer-title">Order Completion Proofs</h2>
                <p>
                  Order {proofViewerOrder.order_id || proofViewerOrder.tikwiz_order_id}
                </p>
              </div>
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => setProofViewerOrder(null)}
              >
                Close
              </button>
            </div>

            <div className="transactions-proof-viewer-body">
              {proofViewerFiles.length > 0 && (
                <nav className="transactions-proof-navigation" aria-label="Proof navigation">
                  <button
                    className="btn btn-secondary"
                    type="button"
                    aria-label="Previous proof"
                    title="Previous proof"
                    disabled={proofViewerIndex <= 0}
                    onClick={() => navigateProof(-1)}
                  >
                    <span aria-hidden="true">←</span>
                  </button>
                  <div className="transactions-proof-caption" aria-live="polite" aria-atomic="true">
                    <strong>{proofViewerIndex + 1} of {proofViewerFiles.length}</strong>
                    <span>{selectedProof?.file_name}</span>
                  </div>
                  <button
                    className="btn btn-secondary"
                    type="button"
                    aria-label="Next proof"
                    title="Next proof"
                    disabled={proofViewerIndex < 0 || proofViewerIndex >= proofViewerFiles.length - 1}
                    onClick={() => navigateProof(1)}
                  >
                    <span aria-hidden="true">→</span>
                  </button>
                </nav>
              )}
              {proofViewerLoading && (
                <p className="transactions-proof-viewer-status">Loading proof...</p>
              )}
              {!proofViewerLoading && proofViewerError && (
                <div className="notification error transactions-completion-error" role="alert">
                  {proofViewerError}
                </div>
              )}
              {!proofViewerLoading && proofViewerUrl && proofViewerIsPdf && (
                <>
                  <iframe
                    className="transactions-proof-viewer-pdf"
                    src={proofViewerUrl}
                    title={`Completion proof PDF for order ${proofViewerOrder.order_id || proofViewerOrder.tikwiz_order_id}`}
                  />
                  <div className="transactions-proof-viewer-actions">
                    <a
                      className="btn btn-secondary"
                      href={proofViewerUrl}
                      download={selectedProof?.file_name || 'proof.pdf'}
                    >
                      Download PDF
                    </a>
                  </div>
                </>
              )}
              {!proofViewerLoading && proofViewerUrl && !proofViewerIsPdf && (
                <img
                  className="transactions-proof-viewer-image"
                  src={proofViewerUrl}
                  alt={`Completion proof for order ${proofViewerOrder.order_id || proofViewerOrder.tikwiz_order_id}`}
                />
              )}
            </div>
          </section>
        </div>
      )}

      {pricingModalOpen && (
        <div className="transactions-pricer-backdrop" role="presentation">
          <section
            className="transactions-pricer-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="transactions-pricer-title"
          >
            <div className="transactions-pricer-header">
              <div>
                <h2 id="transactions-pricer-title">Order Pricer</h2>
                <p>
                  Review the latest result, preview pending orders, or commit and undo pricing.
                  {pricingLimit ? ` This preview is limited to the most recent ${pricingLimit} Price orders.` : ''}
                </p>
              </div>
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => setPricingModalOpen(false)}
                disabled={pricing}
              >
                Close
              </button>
            </div>

            <div className="transactions-pricer-body">
              {pricingRunLoading ? (
                <div className="transactions-pricer-empty">Loading the last pricing run...</div>
              ) : pricingRun ? (
                <>
                  <div className="transactions-pricer-run-heading">
                    <div>
                      <strong>{pricingRun.isDryRun ? 'Dry-run preview' : 'Last committed run'}</strong>
                      <span className={`transactions-pricer-status status-${pricingRun.runStatus || 'completed'}`}>
                        {pricingRun.previewInvalidated
                          ? 'Invalidated'
                          : pricingRun.isDryRun ? 'Not saved' : (pricingRun.runStatus || 'completed')}
                      </span>
                    </div>
                    <p>
                      {pricingRun.previewInvalidated
                        ? 'The alias change updated an order. Run a new Dry Run before pricing.'
                        : pricingRun.isDryRun
                        ? 'No order or unit changes were committed.'
                        : `${formatPricingTimestamp(pricingRun.completedAt)}${pricingRun.initiatedByEmail ? ` by ${getApiUserCode(pricingRun.initiatedByEmail)}` : ''}`}
                    </p>
                    {pricingRun.runStatus === 'undone' && (
                      <p>
                        Undone {formatPricingTimestamp(pricingRun.undoneAt)}
                        {pricingRun.undoneByEmail ? ` by ${getApiUserCode(pricingRun.undoneByEmail)}` : ''}.
                      </p>
                    )}
                  </div>

                  <div className="transactions-pricer-summary">
                    <div><span>Processed</span><strong>{pricingRun.processedCount}</strong></div>
                    <div><span>{pricingRun.isDryRun ? 'Would price' : 'Priced'}</span><strong>{pricingRun.pricedCount}</strong></div>
                    <div><span>{pricingRun.isDryRun ? 'Would move to To Buy' : 'To Buy'}</span><strong>{pricingRun.toBuyCount}</strong></div>
                    {pricingRun.voidRequiredCount > 0 && (
                      <div><span>{pricingRun.isDryRun ? 'Needs To Buy approval' : 'Still needs To Buy approval'}</span><strong>{pricingRun.voidRequiredCount}</strong></div>
                    )}
                    {pricingRun.manualResolutionCount > 0 && (
                      <div className="manual-resolution"><span>Manual resolution</span><strong>{pricingRun.manualResolutionCount}</strong></div>
                    )}
                    <div><span>Already priced</span><strong>{pricingRun.alreadyPricedCount}</strong></div>
                    <div><span>Skipped</span><strong>{pricingRun.skippedCount}</strong></div>
                    <div><span>{pricingRun.isDryRun ? 'Units to claim' : 'Units claimed'}</span><strong>{pricingRun.unitsClaimed}</strong></div>
                  </div>

                  {pricingRun.manualResolutionCount > 0 && (
                    <div className="transactions-pricer-manual-warning" role="alert">
                      {pricingRun.manualResolutionCount} order{pricingRun.manualResolutionCount === 1 ? '' : 's'} require manual resolution and were not changed. Review the dedicated section below before continuing.
                    </div>
                  )}

                  {(pricingRun.items || []).length > 0 ? (
                    <div className="transactions-pricer-results">
                      {groupPricingResults(pricingRun.items || []).map((group) => (
                        <section className={`transactions-pricer-group group-${group.key}`} key={group.key}>
                          <h3>{group.label} <span>{group.items.length}</span></h3>
                          <div className="transactions-pricer-group-items">
                            {group.items.map((item, index) => (
                              <article
                                className={`transactions-pricer-item action-${item.action}`}
                                key={`${item.tikwiz_order_id || item.order_id || index}-${item.action}`}
                              >
                                <div className="transactions-pricer-item-header">
                                  <div>
                                    <strong>{item.order_id || item.tikwiz_order_id || 'Unknown order'}</strong>
                                    <span>{item.event_name || 'Unknown event'} · {item.venue_name || 'Unknown venue'} · {item.type_label || 'No type'}</span>
                                  </div>
                                  <span className="transactions-pricer-action">{item.action.replace('_', ' ')}</span>
                                </div>
                                <p className="transactions-pricer-message">{item.message}</p>

                                {item.requires_void_approval && (
                                  <label className="transactions-pricer-void-approval">
                                    <input
                                      type="checkbox"
                                      checked={approvedVoidOrderIds.includes(item.tikwiz_order_id)}
                                      onChange={() => toggleVoidApproval(item.tikwiz_order_id)}
                                      disabled={pricing}
                                    />
                                    <span>
                                      Approve writing VOID units and move this order to To Buy
                                      {item.units_created > 0 ? ` (${item.units_created} new unit${item.units_created === 1 ? '' : 's'})` : ''}
                                    </span>
                                  </label>
                                )}

                                {(item.changes || []).length > 0 && (
                                  <div className="transactions-pricer-changes">
                                    {(item.changes || []).map((change) => (
                                      <div key={change.field}>
                                        <span>{change.field}</span>
                                        <code>{formatPricingChangeValue(change.before)}</code>
                                        <span aria-hidden="true">→</span>
                                        <code>{formatPricingChangeValue(change.after)}</code>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {((item.unit_codes || []).length > 0 || (item.unit_ids || []).length > 0) && (
                                  <p className="transactions-pricer-units">
                                    Units: {(item.unit_codes?.length ? item.unit_codes : item.unit_ids || []).join(', ')}
                                  </p>
                                )}

                                {item.action === 'skipped' && item.type_label && item.tikwiz_order_id
                                  && String(item.message || '').startsWith('No matching event position') && (
                                  <OrderAliasButton
                                    aliasAdded={aliasAddedOrderIds.includes(item.tikwiz_order_id)}
                                    onClick={() => openTicketTypeAliasModal(item)}
                                    disabled={aliasSaving || pricing}
                                  />
                                )}
                              </article>
                            ))}
                          </div>
                        </section>
                      ))}
                    </div>
                  ) : (
                    <div className="transactions-pricer-empty">This run found no orders in Price status.</div>
                  )}
                </>
              ) : (
                <div className="transactions-pricer-empty">No committed Price Orders run has been recorded yet.</div>
              )}
            </div>

            {error && <div className="notification error transactions-pricer-error">{error}</div>}

            <div className="transactions-pricer-actions">
              <button
                className="btn btn-danger"
                type="button"
                onClick={handleUndoLastPricer}
                disabled={pricing || pricingRunLoading || !latestCommittedPricingRun?.canUndo}
              >
                {pricing ? 'Working...' : 'Undo Last Pricer'}
              </button>
              {canViewPricingLogs(currentUser) && (
                <Link className="btn btn-secondary" to="/user/transactions/orders/pricing-history" target="_blank" rel="noopener noreferrer">
                  Pricing history &amp; logs
                </Link>
              )}
              <button
                className="btn btn-secondary"
                type="button"
                onClick={handleDryRunOrders}
                disabled={pricing || pricingRunLoading}
              >
                {pricing ? 'Working...' : 'Dry Run'}
              </button>
              <span className={`transactions-pricer-dry-run-status ${pricingRun?.isDryRun && !pricingRun.previewInvalidated && pricingRun.processedCount > 0 ? 'success' : ''}`}>
                {pricingRun?.previewInvalidated
                  ? 'Dry run invalidated by alias change. Run Dry Run again.'
                  : pricingRun?.isDryRun
                  ? (pricingRun.processedCount > 0
                    ? 'Dry run successful. Pricing enabled.'
                    : 'Dry run completed with no Price orders.')
                  : 'Run Dry Run to enable pricing.'}
              </span>
              <button
                className="btn btn-primary"
                type="button"
                onClick={handlePriceOrders}
                disabled={pricing || pricingRunLoading || !pricingRun?.isDryRun || pricingRun.previewInvalidated || pricingRun.processedCount <= 0}
                title={!pricingRun?.isDryRun || pricingRun.previewInvalidated ? 'Run Dry Run first' : undefined}
              >
                {pricing ? 'Working...' : 'Price Orders'}
              </button>
            </div>
          </section>
        </div>
      )}

      {aliasDraft && (
        <div className="transactions-alias-backdrop">
          <form className="transactions-alias-modal" onSubmit={handleSaveTicketTypeAlias}>
            <div className="transactions-alias-modal-header">
              <div>
                <h2>Ticket Type Alias</h2>
                <p>{aliasDraft.orderId || aliasDraft.tikwizOrderId || 'Order'}</p>
              </div>
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => setAliasDraft(null)}
                disabled={aliasSaving}
              >
                Cancel
              </button>
            </div>

            <div className="transactions-alias-context">
              <span>{aliasDraft.eventName || 'Unknown event'}</span>
              <span>{aliasDraft.venueName || 'Unknown venue'}</span>
            </div>

            <label htmlFor="transactions-alias-name">Alias</label>
            <input
              id="transactions-alias-name"
              type="text"
              value={aliasDraft.aliasName}
              onChange={(event) => setAliasDraft((current) => (
                current ? { ...current, aliasName: event.target.value } : current
              ))}
              disabled={aliasSaving}
            />

            <label htmlFor="transactions-canonical-ticket-type">Canonical Type</label>
            <input
              id="transactions-canonical-ticket-type"
              type="text"
              list="transactions-canonical-ticket-type-options"
              value={aliasDraft.canonicalName}
              onChange={(event) => setAliasDraft((current) => (
                current ? { ...current, canonicalName: event.target.value } : current
              ))}
              disabled={aliasSaving || aliasOptionsLoading}
            />
            <datalist id="transactions-canonical-ticket-type-options">
              {ticketTypeNames.map((name) => (
                <option key={name} value={name} />
              ))}
            </datalist>

            {aliasOptionsLoading && (
              <p className="transactions-alias-options-status">Loading canonical ticket types...</p>
            )}
            {aliasOptionsError && (
              <div className="notification error">{aliasOptionsError}</div>
            )}

            <div className="transactions-alias-actions">
              <button className="btn btn-primary" type="submit" disabled={aliasSaving || aliasOptionsLoading}>
                {aliasSaving ? 'Saving...' : 'Save Alias'}
              </button>
            </div>
          </form>
        </div>
      )}

      {purchaseVenueDraft && (
        <div className="venues-modal-backdrop">
          <div className="venues-modal">
            <div className="venues-modal-header">
              <div>
                <h2>{purchaseVenueDraft.venueId ? 'Venue Details Required' : 'Resolve Venue'}</h2>
                <p className="venues-modal-subtitle">
                  {purchaseVenueDraft.venueId ? 'Add the missing location details' : 'Create this venue or map it to an existing canonical venue'} before pricing{' '}
                  {VENUE_PROMPT_RECORD_TYPES[purchaseVenueDraft.recordType].label}
                  {purchaseVenueDraft.recordIds.length === 1 ? '' : 's'}{' '}
                  {purchaseVenueDraft.recordIds.join(', ')}.
                  {purchaseVenueQueue.length > 0 ? ` ${purchaseVenueQueue.length} more venue${purchaseVenueQueue.length === 1 ? '' : 's'} will follow.` : ''}
                </p>
              </div>
              <button
                className="venues-modal-close"
                type="button"
                onClick={closePurchaseVenuePrompt}
                disabled={purchaseVenueSaving}
                aria-label="Close venue details prompt"
              >
                X
              </button>
            </div>

            <form className="venues-modal-form" onSubmit={handleSavePurchaseVenue}>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label htmlFor="purchase-pricing-venue-name">Current Venue Value</label>
                <input
                  id="purchase-pricing-venue-name"
                  type="text"
                  value={purchaseVenueDraft.name}
                  readOnly
                />
              </div>

              {!purchaseVenueDraft.venueId && (
                <fieldset className="transactions-venue-resolution-mode">
                  <legend>Resolve as</legend>
                  <label className={purchaseVenueDraft.resolutionMode === 'create' ? 'is-selected' : ''}>
                    <input
                      type="radio"
                      name="purchase-pricing-venue-resolution"
                      value="create"
                      checked={purchaseVenueDraft.resolutionMode === 'create'}
                      onChange={() => handlePurchaseVenueFieldChange('resolutionMode', 'create')}
                      disabled={purchaseVenueSaving}
                    />
                    <span>
                      <strong>Create new venue</strong>
                      <small>Add this value as a new canonical venue.</small>
                    </span>
                  </label>
                  <label className={purchaseVenueDraft.resolutionMode === 'alias' ? 'is-selected' : ''}>
                    <input
                      type="radio"
                      name="purchase-pricing-venue-resolution"
                      value="alias"
                      checked={purchaseVenueDraft.resolutionMode === 'alias'}
                      onChange={() => handlePurchaseVenueFieldChange('resolutionMode', 'alias')}
                      disabled={purchaseVenueSaving || canonicalVenuesLoading}
                    />
                    <span>
                      <strong>Alias existing venue</strong>
                      <small>Map this value to an existing canonical venue.</small>
                    </span>
                  </label>
                </fieldset>
              )}

              {!purchaseVenueDraft.venueId && purchaseVenueDraft.resolutionMode === 'alias' ? (
                <div className="form-group transactions-venue-canonical-select">
                  <label htmlFor="purchase-pricing-canonical-venue">Existing Canonical Venue</label>
                  <select
                    id="purchase-pricing-canonical-venue"
                    value={purchaseVenueDraft.canonicalVenueId}
                    onChange={(event) => handlePurchaseVenueFieldChange('canonicalVenueId', event.target.value)}
                    disabled={purchaseVenueSaving || canonicalVenuesLoading}
                    autoFocus
                  >
                    <option value="">
                      {canonicalVenuesLoading ? 'Loading venues...' : 'Select canonical venue'}
                    </option>
                    {canonicalVenues.map((venue) => (
                      <option key={venue.venue_id} value={venue.venue_id}>
                        {venue.name}{venue.location ? ` - ${venue.location}` : ''}{venue.country ? `, ${venue.country}` : ''}
                      </option>
                    ))}
                  </select>
                  <small className="transactions-venue-alias-help">
                    &quot;{purchaseVenueDraft.name}&quot; will be saved as an alias of the selected venue.
                  </small>
                </div>
              ) : (
                <>
                  <div className="form-group">
                    <label htmlFor="purchase-pricing-venue-location">Location</label>
                    <input
                      id="purchase-pricing-venue-location"
                      type="text"
                      value={purchaseVenueDraft.location}
                      onChange={(event) => handlePurchaseVenueFieldChange('location', event.target.value)}
                      placeholder="City or area"
                      disabled={purchaseVenueSaving}
                      autoFocus
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="purchase-pricing-venue-country">Country</label>
                    <input
                      id="purchase-pricing-venue-country"
                      type="text"
                      value={purchaseVenueDraft.country}
                      onChange={(event) => handlePurchaseVenueFieldChange('country', event.target.value)}
                      disabled={purchaseVenueSaving}
                    />
                  </div>

                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label htmlFor="purchase-pricing-venue-twz-location">TWZ Location</label>
                    <select
                      id="purchase-pricing-venue-twz-location"
                      value={purchaseVenueDraft.twzLocation}
                      onChange={(event) => handlePurchaseVenueFieldChange('twzLocation', event.target.value)}
                      disabled={purchaseVenueSaving}
                    >
                      <option value="">Select TWZ Location</option>
                      {TWZ_LOCATION_OPTIONS.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {purchaseVenueDraft.resolutionMode === 'alias' && canonicalVenuesError && (
                <div className="notification error">{canonicalVenuesError}</div>
              )}

              {purchaseVenueError && (
                <div className="notification error">{purchaseVenueError}</div>
              )}

              <div className="venues-modal-actions">
                <button className="btn btn-secondary" type="button" onClick={closePurchaseVenuePrompt} disabled={purchaseVenueSaving}>
                  Cancel
                </button>
                <button className="btn btn-primary" type="submit" disabled={purchaseVenueSaving}>
                  {purchaseVenueSaving
                    ? 'Saving...'
                    : purchaseVenueDraft.resolutionMode === 'alias'
                      ? purchaseVenueQueue.length > 0 ? 'Add Alias & Continue' : 'Add Alias & Resume Pricing'
                      : purchaseVenueQueue.length > 0 ? 'Save & Continue' : 'Save & Resume Pricing'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionsPage;
